import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useHistory } from 'react-router-dom';
import AccountProfile from 'src/components/account/AccountProfile';
import AccountProfileDetails from 'src/components/account/AccountProfileDetails';
import { LogoutModal } from 'src/common/AccountPopover';
import useAuth from 'src/context/AuthContext';
import { USER_ROLES } from 'src/utils/constants';
import ROUTE from 'src/utils/routeConstants';

const PROFILE_FORM_ID = 'profile-editor-form';

const ProfileEditorDialog = ({ open, onClose }) => {
  const history = useHistory();
  const { getUser } = useAuth();
  const user = getUser();
  const isOperator = user?.role === USER_ROLES.operator;
  const [saving, setSaving] = useState(false);
  const [showLogout, setShowLogout] = useState(false);

  const handleSettings = () => {
    onClose();
    history.push(ROUTE.settings);
  };

  const handleSignOut = () => setShowLogout(true);

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        fullWidth
        maxWidth={false}
        scroll="paper"
        aria-labelledby="profile-editor-dialog-title"
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            borderRadius: { xs: 0, sm: 3 },
            height: { xs: 'calc(100vh - 16px)', md: '86vh' },
            maxHeight: { xs: 'calc(100vh - 16px)', md: '86vh' },
            maxWidth: 'none',
            width: { xs: 'calc(100vw - 16px)', md: 'min(1240px, calc(100vw - 48px))' },
            overflow: 'hidden',
            boxShadow: '0 24px 70px rgba(15, 35, 75, 0.20)'
          }
        }}
      >
        <DialogTitle
          id="profile-editor-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexShrink: 0,
            borderBottom: '1px solid',
            borderColor: '#E8EEF7',
            px: { xs: 2.5, sm: 4 },
            py: { xs: 2.5, sm: 3 }
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#10213D' }}>
              Profile
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: '#64748B' }}>
              Manage your profile details and account preferences.
            </Typography>
          </Box>
          <IconButton
            aria-label="Close profile editor"
            onClick={onClose}
            disabled={saving}
            edge="end"
            sx={{ color: '#64748B', mt: -0.5 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            minHeight: 0,
            overflowY: 'auto',
            bgcolor: '#FFFFFF'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'stretch',
              minHeight: '100%'
            }}
          >
            <Box
              component="aside"
              sx={{
                width: { xs: '100%', md: 392 },
                flexShrink: 0,
                p: { xs: 2, sm: 3 },
                bgcolor: '#F3F7FF',
                background: 'linear-gradient(180deg, #F5F9FF 0%, #EEF5FF 100%)',
                borderRight: { md: '1px solid #E8EEF7' },
                borderBottom: { xs: '1px solid #E8EEF7', md: 'none' }
              }}
            >
              <AccountProfile dialog sx={{ mb: 2 }} />
              <Divider sx={{ borderColor: '#DCE6F4', mb: 2 }} />
              <List disablePadding>
                <ListItemButton
                  selected
                  sx={{
                    mb: 1,
                    borderRadius: 2,
                    minHeight: 72,
                    px: 1.5,
                    color: '#2563EB',
                    '&.Mui-selected': {
                      bgcolor: '#E5EEFF',
                      '&:hover': { bgcolor: '#DCE8FF' }
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 46, color: 'inherit' }}>
                    <PersonOutlineIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Profile Details"
                    secondary="Update your personal information"
                    primaryTypographyProps={{ fontWeight: 800 }}
                    secondaryTypographyProps={{ sx: { mt: 0.25, color: '#52627A' } }}
                  />
                  <ArrowForwardIosRoundedIcon sx={{ fontSize: 16, color: 'inherit' }} />
                </ListItemButton>
                {!isOperator && (
                  <ListItemButton
                    onClick={handleSettings}
                    sx={{
                      mb: 1,
                      borderRadius: 2,
                      minHeight: 72,
                      px: 1.5,
                      color: '#52627A',
                      '&:hover': { bgcolor: '#EAF1FF', color: '#2563EB' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 46, color: 'inherit' }}>
                      <SettingsOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Settings"
                      secondary="Manage your preferences"
                      primaryTypographyProps={{ fontWeight: 800 }}
                      secondaryTypographyProps={{ sx: { mt: 0.25, color: '#52627A' } }}
                    />
                    <ArrowForwardIosRoundedIcon sx={{ fontSize: 16, color: 'inherit' }} />
                  </ListItemButton>
                )}
                <ListItemButton
                  onClick={handleSignOut}
                  sx={{
                    borderRadius: 2,
                    minHeight: 72,
                    px: 1.5,
                    color: '#DC2626',
                    '&:hover': { bgcolor: '#FFF1F2', color: '#B91C1C' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 46, color: 'inherit' }}>
                    <LogoutOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Sign out"
                    secondary="Sign out from your account"
                    primaryTypographyProps={{ fontWeight: 800 }}
                    secondaryTypographyProps={{ sx: { mt: 0.25, color: '#52627A' } }}
                  />
                  <ArrowForwardIosRoundedIcon sx={{ fontSize: 16, color: 'inherit' }} />
                </ListItemButton>
              </List>
            </Box>
            <Box
              component="section"
              sx={{
                flex: 1,
                minWidth: 0,
                p: { xs: 2, sm: 3, md: 4 },
                bgcolor: '#FFFFFF'
              }}
            >
              <AccountProfileDetails
                dialog
                formId={PROFILE_FORM_ID}
                onSavingChange={setSaving}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'flex-end',
            gap: 1.5,
            flexShrink: 0,
            px: { xs: 2.5, sm: 4 },
            py: { xs: 2, sm: 2.5 },
            borderTop: '1px solid #E8EEF7',
            bgcolor: '#FFFFFF'
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={saving}
            sx={{
              minWidth: 120,
              minHeight: 48,
              borderColor: '#D4DEED',
              color: '#52627A',
              borderRadius: 2
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={PROFILE_FORM_ID}
            variant="contained"
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />
            }
            sx={{
              minWidth: 170,
              minHeight: 48,
              borderRadius: 2,
              boxShadow: '0 8px 18px rgba(42, 99, 218, 0.22)'
            }}
          >
            Save Details
          </Button>
        </DialogActions>
      </Dialog>
      <LogoutModal open={showLogout} onClose={() => setShowLogout(false)} />
    </>
  );
};

ProfileEditorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ProfileEditorDialog;
